import React, { useState, Fragment, useMemo } from "react";
import { useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import Chip from "@mui/material/Chip";
import {
  Card,
  CardMedia,
  CardActions,
  IconButton,
  Typography,
  CardActionArea,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  DialogContentText,
  Button,
} from "@material-ui/core";
import MoreVertIcon from "@material-ui/icons/MoreVert";

import { RootState } from "../../store/store";
import ImageCardGroupMenu from "./ImageCardGroupMenu";
import ImageGroupSelectModal from "./ImageGroupSelectModal";

import DELETE_ASSET from "../../graphql/DELETE_ASSET";
import { Reference, StoreObject, useMutation } from "@apollo/client";

function ImageCardGroup(props: ImageCardGroupProps) {
  const state = useSelector((state: RootState) => state);
  const [settingsAnchor, setSettingsAnchor] = useState<null | HTMLElement>(
    null
  );
  const [openModal, setOpenModal] = useState(false);
  const [openDeleteWarning, setOpenDeleteWarning] = useState(false);

  const classes = useStyles();

  const handleClickOpenDelete = () => {
    setOpenDeleteWarning(true);
  };

  const handleCloseDelete = () => {
    setOpenDeleteWarning(false);
  };

  const handleDeleteAndClose = async () => {
    await deleteImage();
  };

  const handleOpenModal = () => {
    //this function has to do nothing if it is a child of the BannerImageSelectorModal
    if (!props.isBannerImageSelectModal) {
      setOpenModal(true);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  // Define the useMutation hook for deleting an asset
  const [deleteAsset, { loading: deleteLoading, error: deleteError }] =
    useMutation(DELETE_ASSET, {
      context: {
        headers: {
          Authorization: `Bearer ${state.current_user.user?.token}`,
          instance: state.current_site.site?.slug,
        },
      },
      // Update the cache after the mutation
      update(cache, { data: { deleteAsset } }) {
        cache.modify({
          fields: {
            assets(existingAssets = [], { readField }) {
              return existingAssets.filter(
                (assetRef: Reference | StoreObject | undefined) =>
                  readField("filePath", assetRef) !== deleteAsset.filePath
              );
            },
          },
        });
      },
    });

  const handleSettingsOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSettingsAnchor(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setSettingsAnchor(null);
  };

  // handles delete for group images
  const deleteImage = async () => {
    // loops over the images in the group and run the delteAsset mutation for each image
    const deletePromises = props.group.map((image: any) => {
      const filePath = `${props.imageFilePath.substring(0, props.imageFilePath.lastIndexOf("/") + 1)}${image.name}`;

      return deleteAsset({
        variables: {
          physical_file_path: filePath,
        },
      })
        .then(() => filePath) // Return filePath after deletion
        .catch((error) => {
          console.error(`Error deleting asset: ${filePath}`, error);
          return null; // Handle the case where deletion fails
        });
    });

    // Wait for all deletions to finish and filter out any failed deletions (null values)
    const deletedFilePaths = (await Promise.all(deletePromises)).filter(
      (filePath): filePath is string => !!filePath
    );
    // Call the handleDeleteGroupAssets function to remove all successfully deleted assets from the state and update the UI
    if (deletedFilePaths.length > 0) {
      props.handleDeleteGroupAssets(deletedFilePaths);
    }
  };

  const name = useMemo(() => {
    return props.imageName.split("/").pop();
  }, [props.imageName]);

  const cardClasses = (): string => {
    if (props.selectedImageUrl && props.selectedImageUrl === props.imageUrl) {
      return classes.card + " " + classes.active;
    }
    return classes.card;
  };

  return (
    <>
      <Fragment>
        <CardActionArea>
          <Card
            variant="outlined"
            className={cardClasses()}
            onClick={() =>
              props.setSelectedImage && props.setSelectedImage(props.imageUrl)
            }
          >
            {/* optimize images group have a label */}
            <Chip label="Optimised" color="primary" className={classes.chip} />

            <CardMedia
              onClick={() => {
                if (!state.modal_reducer.isOpen) {
                  handleOpenModal();
                } else {
                  props.handleCopyToClipboard(props.imageUrl);
                }
              }}
              className={classes.media}
              image={props.imageUrl} // render large image url
              title={name} // render large image name
            />

            <CardActions className={classes.actions}>
              <Typography
                variant="subtitle1"
                component="h2"
                className={classes.heading}
              >
                {/* cleans the file name to hide -large or -medium appended to the end due the compression */}
                {truncate(
                  name?.includes("-large")
                    ? name.split("-large")[0]
                    : (name?.split("-medium")[0] as string)
                )}
              </Typography>
              <IconButton onClick={handleSettingsOpen}>
                <MoreVertIcon />
              </IconButton>
            </CardActions>
          </Card>

          <ImageCardGroupMenu
            group={props.group}
            anchor={settingsAnchor}
            handleClose={handleSettingsClose}
            handleClickOpenDelete={handleClickOpenDelete}
          />
        </CardActionArea>
        {!state.modal_reducer.isOpen && (
          <ImageGroupSelectModal
            open={openModal}
            onClose={handleCloseModal}
            handleCopyToClipboard={props.handleCopyToClipboard}
            onDelete={deleteImage}
            handleClickOpenDelete={handleClickOpenDelete}
            group={props.group}
          />
        )}
      </Fragment>
      <Dialog open={openDeleteWarning} onClose={handleCloseDelete}>
        <DialogTitle>{"Delete Image"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you wish to delete these images? This will delete all
            optimised images and this action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelete} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteAndClose} color="secondary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

const useStyles = makeStyles((theme) => ({
  card: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },
  active: {
    border: "solid 2px",
    borderColor: theme.palette.primary.main,
  },
  heading: {
    overflow: "hidden",
  },
  media: {
    height: "200px",
    objectFit: "cover",
  },
  actions: {
    display: "flex",
    justifyContent: "space-between",
    height: "60px",
    overflow: "hidden",
    padding: "0 1rem",
  },
  chip: {
    position: "absolute",
    top: theme.spacing(1),
    left: theme.spacing(1),
    zIndex: 1,
  },
}));

interface ImageCardGroupProps {
  imageUrl: string;
  imageFilePath: string;
  imageName: string;
  group: AssetItem[];
  setSelectedImage?: (url: string) => void;
  selectedImageUrl?: string;
  handleDeleteGroupAssets: (filePaths: string[]) => void;
  handleCopyToClipboard: (url: string) => void;
  isBannerImageSelectModal?: boolean;
}

interface AssetItem {
  created_at: string;
  id: string;
  metadata: Metadata;
  name: string;
  physical_file_path: string;
  updated_at: string;
  url?: string; // Make this optional in case it's nested under the `asset` property.
  asset?: {
    url: string;
    physical_file_path: string;
    name: string;
  }; // Optional in case the asset details are nested inside another object.
  type?: string; // Optional in case the `type` field is used in some parts.
}

interface Metadata {
  group_id?: string;
}

function truncate(str: string) {
  return str.length > 19 ? str.substring(0, 19) + "..." : str;
}

export default ImageCardGroup;
